import { useState, useEffect, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { useAuth } from '../context/AuthContext'
import MainLayout from '../components/MainLayout'
import SortableNoteCard from '../components/SortableNoteCard'
import NoteCard from '../components/NoteCard'
import { Note, getNotes, updateNoteOrder } from '../api/notes'
import apiClient from '../api/client'

const HOME_COLUMNS_KEY = 'fastnotes_home_columns'

const getSavedColumns = (): 1 | 2 | 3 => {
  try {
    const saved = localStorage.getItem(HOME_COLUMNS_KEY)
    if (saved === '1' || saved === '2' || saved === '3') return parseInt(saved, 10) as 1 | 2 | 3
  } catch {
    // ignore
  }
  return 2 // default: 2 columns
}

/** Sort notes by their `order` field (nulls go to the end). */
function sortByOrder(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => {
    if (a.order == null && b.order == null) return 0
    if (a.order == null) return 1
    if (b.order == null) return -1
    return a.order - b.order
  })
}

export default function HomePage() {
  const { user } = useAuth()
  const [notes, setNotes] = useState<Note[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeNote, setActiveNote] = useState<Note | null>(null)
  const [isSavingOrder, setIsSavingOrder] = useState(false)
  const [columns, setColumns] = useState<1 | 2 | 3>(getSavedColumns)

  const handleColumnsChange = (n: 1 | 2 | 3) => {
    setColumns(n)
    try { localStorage.setItem(HOME_COLUMNS_KEY, String(n)) } catch { /* ignore */ }
  }

  // MouseSensor for desktop (Playwright CDP triggers mousedown reliably)
  // TouchSensor for mobile touch devices
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  )

  const fetchHomeNotes = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getNotes({ view_status: 'homed' }, 'order', 'asc')
      setNotes(sortByOrder(response.data))
    } catch (err) {
      console.error('Failed to fetch home notes:', err)
      setError('Failed to load notes. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHomeNotes()
  }, [fetchHomeNotes])

  // ── Drag handlers ─────────────────────────────────────────────────────────

  const handleDragStart = (event: DragStartEvent) => {
    const note = notes.find(n => n.id === event.active.id)
    setActiveNote(note ?? null)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveNote(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = notes.findIndex(n => n.id === active.id)
    const newIndex = notes.findIndex(n => n.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    // Optimistically reorder in UI
    const reordered = arrayMove(notes, oldIndex, newIndex)
    setNotes(reordered)

    // Persist to backend
    setIsSavingOrder(true)
    try {
      await updateNoteOrder(reordered.map(n => n.id))
    } catch (err) {
      console.error('Failed to save order:', err)
      // Roll back on error
      setNotes(notes)
    } finally {
      setIsSavingOrder(false)
    }
  }

  // ── Note action handlers ──────────────────────────────────────────────────

  const handleToggleStar = async (noteId: string) => {
    const note = notes.find(n => n.id === noteId)
    if (!note) return
    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [noteId],
        note_partial: { is_starred: !note.is_starred },
      })
      setNotes(notes.map(n => n.id === noteId ? { ...n, is_starred: !n.is_starred } : n))
    } catch (err) {
      console.error('Failed to toggle star:', err)
    }
  }

  const handleToggleHome = async (noteId: string) => {
    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [noteId],
        note_partial: { is_home: false },
      })
      setNotes(notes.filter(n => n.id !== noteId))
    } catch (err) {
      console.error('Failed to remove from home:', err)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const gridClass = columns === 1
    ? 'grid grid-cols-1 gap-3 sm:gap-4'
    : columns === 3
      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'
      : 'grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'

  return (
    <MainLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back{user?.full_name ? `, ${user.full_name}` : ''}!
          </h1>
          <p className="text-gray-600 mt-1">
            Here are your pinned notes
            {notes.length > 1 && (
              <span className="ml-2 text-xs text-gray-400">(drag to reorder)</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Saving indicator */}
          {isSavingOrder && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              Saving order…
            </div>
          )}

          {/* Column count toggle: 1 / 2 / 3 */}
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => handleColumnsChange(1)}
              data-testid="home-cols-1"
              title="Single column"
              className={`p-2 rounded-lg transition-colors ${
                columns === 1 ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {/* list / single-col icon */}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => handleColumnsChange(2)}
              data-testid="home-cols-2"
              title="Two columns"
              className={`p-2 rounded-lg transition-colors ${
                columns === 2 ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {/* 2×2 grid icon */}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => handleColumnsChange(3)}
              data-testid="home-cols-3"
              title="Three columns"
              className={`p-2 rounded-lg transition-colors ${
                columns === 3 ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {/* 3-col grid icon */}
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="2" y="3" width="6" height="7" rx="1"/>
                <rect x="9" y="3" width="6" height="7" rx="1"/>
                <rect x="16" y="3" width="6" height="7" rx="1"/>
                <rect x="2" y="13" width="6" height="7" rx="1"/>
                <rect x="9" y="13" width="6" height="7" rx="1"/>
                <rect x="16" y="13" width="6" height="7" rx="1"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-red-700">{error}</span>
            <button
              onClick={fetchHomeNotes}
              className="ml-auto text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div className={gridClass}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
              <div className="p-3 sm:p-4 border-b border-gray-100">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="flex gap-1">
                  <div className="h-4 bg-gray-100 rounded w-12" />
                  <div className="h-4 bg-gray-100 rounded w-16" />
                </div>
              </div>
              <div className="p-3 sm:p-4">
                <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                <div className="h-4 bg-gray-100 rounded w-5/6 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-4/6" />
              </div>
              <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border-t border-gray-100">
                <div className="h-3 bg-gray-100 rounded w-20" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && notes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No home notes yet</h3>
          <p className="text-gray-500 max-w-sm">
            Pin your favorite notes to the home screen for quick access.
          </p>
        </div>
      )}

      {/* Sortable grid */}
      {!isLoading && notes.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={notes.map(n => n.id)} strategy={rectSortingStrategy}>
            <div
              data-testid="home-notes-grid"
              className={gridClass}
            >
              {notes.map(note => (
                <SortableNoteCard
                  key={note.id}
                  note={note}
                  onToggleStar={handleToggleStar}
                  onToggleHome={handleToggleHome}
                />
              ))}
            </div>
          </SortableContext>

          {/* Drag overlay — renders the card being dragged at pointer position */}
          <DragOverlay>
            {activeNote && (
              <div className="shadow-2xl rounded-xl opacity-90 rotate-1 scale-105">
                <NoteCard note={activeNote} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </MainLayout>
  )
}
